import React, { useRef, useState } from "react";
import { Box, Button, ButtonGroup, Tooltip, CircularProgress } from "@mui/material";
import {
  FormatBold,
  FormatItalic,
  FormatUnderlined,
  FormatStrikethrough,
  FormatListBulleted,
  FormatListNumbered,
  Code,
  FormatQuote,
  ImageOutlined,
} from "@mui/icons-material";

const EditorToolbar = ({ editor, onImageUpload }) => {
  const fileInputRef = useRef(null);
  const [isUploading, setIsUploading] = useState(false);

  if (!editor) {
    return null;
  }

  const handleImageClick = () => {
    fileInputRef.current?.click();
  };

  const handleFileChange = async (event) => {
    const file = event.target.files?.[0];
    if (!file || !onImageUpload) return;

    setIsUploading(true);
    try {
      const imageUrl = await onImageUpload(file);
      if (imageUrl) {
        editor.chain().focus().setImage({ src: imageUrl, width: 300 }).run();
      }
    } catch (error) {
      console.error("Failed to upload image:", error);
    } finally {
      setIsUploading(false);
      // Reset file input
      if (fileInputRef.current) {
        fileInputRef.current.value = "";
      }
    }
  };

  return (
    <Box
      sx={(theme) => ({
        display: "flex",
        gap: 1,
        mb: 2,
        p: 1,
        backgroundColor: theme.palette.mode === "dark" ? "#2a2a2a" : "#f5f5f5",
        borderRadius: "4px",
        flexWrap: "wrap",
      })}
    >
      <ButtonGroup size="small" variant="outlined">
        <Tooltip title="Bold">
          <Button
            onClick={() => editor.chain().focus().toggleBold().run()}
            disabled={!editor.can().chain().focus().toggleBold().run()}
            variant={editor.isActive("bold") ? "contained" : "outlined"}
          >
            <FormatBold fontSize="small" />
          </Button>
        </Tooltip>

        <Tooltip title="Italic">
          <Button
            onClick={() => editor.chain().focus().toggleItalic().run()}
            disabled={!editor.can().chain().focus().toggleItalic().run()}
            variant={editor.isActive("italic") ? "contained" : "outlined"}
          >
            <FormatItalic fontSize="small" />
          </Button>
        </Tooltip>

        <Tooltip title="Underline">
          <Button
            onClick={() => editor.chain().focus().toggleUnderline().run()}
            disabled={!editor.can().chain().focus().toggleUnderline().run()}
            variant={editor.isActive("underline") ? "contained" : "outlined"}
          >
            <FormatUnderlined fontSize="small" />
          </Button>
        </Tooltip>

        <Tooltip title="Strikethrough">
          <Button
            onClick={() => editor.chain().focus().toggleStrike().run()}
            disabled={!editor.can().chain().focus().toggleStrike().run()}
            variant={editor.isActive("strike") ? "contained" : "outlined"}
          >
            <FormatStrikethrough fontSize="small" />
          </Button>
        </Tooltip>
      </ButtonGroup>

      <ButtonGroup size="small" variant="outlined">
        <Tooltip title="Bullet List">
          <Button
            onClick={() => editor.chain().focus().toggleBulletList().run()}
            variant={editor.isActive("bulletList") ? "contained" : "outlined"}
          >
            <FormatListBulleted fontSize="small" />
          </Button>
        </Tooltip>

        <Tooltip title="Numbered List">
          <Button
            onClick={() => editor.chain().focus().toggleOrderedList().run()}
            variant={editor.isActive("orderedList") ? "contained" : "outlined"}
          >
            <FormatListNumbered fontSize="small" />
          </Button>
        </Tooltip>
      </ButtonGroup>

      <ButtonGroup size="small" variant="outlined">
        <Tooltip title="Code">
          <Button
            onClick={() => editor.chain().focus().toggleCode().run()}
            disabled={!editor.can().chain().focus().toggleCode().run()}
            variant={editor.isActive("code") ? "contained" : "outlined"}
          >
            <Code fontSize="small" />
          </Button>
        </Tooltip>

        <Tooltip title="Blockquote">
          <Button
            onClick={() => editor.chain().focus().toggleBlockquote().run()}
            variant={editor.isActive("blockquote") ? "contained" : "outlined"}
          >
            <FormatQuote fontSize="small" />
          </Button>
        </Tooltip>
      </ButtonGroup>

      <ButtonGroup size="small" variant="outlined">
        <Tooltip title="Insert Image">
          <Button
            onClick={handleImageClick}
            disabled={isUploading}
            variant={editor.isActive("image") ? "contained" : "outlined"}
          >
            {isUploading ? <CircularProgress size={20} /> : <ImageOutlined fontSize="small" />}
          </Button>
        </Tooltip>
      </ButtonGroup>

      <ButtonGroup size="small" variant="outlined">
        <Tooltip title="Undo">
          <Button
            onClick={() => editor.chain().focus().undo().run()}
            disabled={!editor.can().chain().focus().undo().run()}
          >
            ↶
          </Button>
        </Tooltip>

        <Tooltip title="Redo">
          <Button
            onClick={() => editor.chain().focus().redo().run()}
            disabled={!editor.can().chain().focus().redo().run()}
          >
            ↷
          </Button>
        </Tooltip>
      </ButtonGroup>

      <ButtonGroup size="small" variant="outlined">
        <Tooltip title="Clear Formatting">
          <Button
            onClick={() => editor.chain().focus().clearNodes().run()}
          >
            Clear
          </Button>
        </Tooltip>
      </ButtonGroup>

      <input
        ref={fileInputRef}
        type="file"
        accept="image/*"
        onChange={handleFileChange}
        style={{ display: "none" }}
      />
    </Box>
  );
};

export default EditorToolbar;
